import React, { useState } from 'react';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';
import { Placement } from '@popperjs/core';
import { useBemCN } from '@gik/core/utils/bemBlock';

export type PopperPlacement = Placement;

const blockName = `popover`;

export interface PopoverProps extends React.HTMLAttributes<HTMLDivElement> {
  trigger: JSX.Element;
  triggerOnClick?(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
  triggerOnMouseEnter?(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
  isOpen: boolean;
  hoverable?: boolean;
  overlap?: boolean;
  noPad?: boolean;
  hasArrowIndicator?: boolean;
  onHover?(): void;
  onClose?(): void;
  children: React.ReactNode;
  portal?: HTMLElement;
  placement?: Placement;
}

export default function Popover({
  isOpen,
  noPad,
  className,
  hoverable,
  overlap = false,
  onHover,
  onClose,
  trigger,
  triggerOnClick,
  triggerOnMouseEnter,
  hasArrowIndicator = true,
  children,
  portal,
  placement = 'bottom',
}: React.PropsWithChildren<PopoverProps>): React.ReactElement {
  const portalRef = React.useRef<HTMLDivElement>(null);
  const bem = useBemCN(blockName);

  const [popoverReferenceElement, setPopoverReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles, attributes } = usePopper(popoverReferenceElement, popperElement, {
    placement,
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
  });

  const onPopperClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => event.stopPropagation();

  function handleClose(ev: React.MouseEvent<HTMLDivElement>) {
    ev.stopPropagation();
    if (onClose) onClose();
  }

  const popperElementDiv = (
    <div
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}
      {...bem('popper', [{ nopad: noPad }, { overlap }, { arrow: hasArrowIndicator }], className)}
      onClick={onPopperClick}
    >
      <div {...bem('popper-content')}>{children}</div>
      {hasArrowIndicator && <div ref={setArrowElement} style={styles.arrow} {...bem('arrow')} />}
    </div>
  );

  const portalContainer = (
    <div ref={portalRef} {...bem('portal', [{ open: isOpen }])} onClick={handleClose}>
      {popperElementDiv}
    </div>
  );

  return (
    <div
      ref={setPopoverReferenceElement}
      {...bem('reference-element')}
      onClick={triggerOnClick}
      onMouseEnter={e => {
        triggerOnMouseEnter && triggerOnMouseEnter(e);
        onHover && onHover();
      }}
      onMouseLeave={() => {
        if (hoverable) {
          onClose();
        }
      }}
    >
      {trigger}
      {isOpen && (hoverable ? popperElementDiv : ReactDOM.createPortal(portalContainer, portal || document.body))}
    </div>
  );
}
